/**
 * Validadores eficientes de documentos/identificadores brasileiros que
 * misturam ou não letras e dígitos. Reunidos num módulo só para serem a FONTE
 * ÚNICA da regra (ValidationService, DetectionService, createReport e cards
 * reusam daqui), em vez de reimplementar o algoritmo em cada camada.
 *
 * Cobre:
 *  - CNPJ no padrão ANTIGO (14 dígitos) E no NOVO padrão ALFANUMÉRICO do
 *    Serpro/Receita (12 posições em [A-Z0-9] + 2 dígitos verificadores).
 *  - Placa no padrão ANTIGO (AAA9999) e no padrão MERCOSUL (AAA9A99).
 *  - Chassi/VIN (17 alfanuméricos, alfabeto VIN sem I/O/Q).
 *
 * Referência do DV do CNPJ alfanumérico: material oficial do Serpro
 * (cnpj-alfanumerico), algoritmo módulo 11 sobre o VALOR ASCII do caractere
 * menos o ASCII de '0' — para dígitos '0'-'9' isso dá 0-9 (idêntico ao CNPJ
 * numérico clássico), e para letras 'A'-'Z' dá 17-42. Os DVs são SEMPRE
 * numéricos. Validado contra os vetores oficiais nos testes.
 */

/** ASCII de '0' — base do valor posicional no cálculo do DV alfanumérico. */
const ASCII_ZERO = '0'.charCodeAt(0);

/** Pesos do DV (módulo 11), do dígito mais à esquerda ao mais à direita. */
const CNPJ_DV_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

/** Comprimento do CNPJ sem os 2 dígitos verificadores. */
const CNPJ_BASE_LENGTH = 12;

/** CNPJ sem máscara (12 base alfanum + 2 DV numéricos). */
const CNPJ_FULL_REGEX = /^[A-Z0-9]{12}\d{2}$/;

/** CNPJ base sem DV (12 alfanuméricos). */
const CNPJ_BASE_REGEX = /^[A-Z0-9]{12}$/;

/** Caracteres de máscara removíveis de um CNPJ. */
const CNPJ_MASK_CHARS = /[.\-/]/g;

/**
 * Remove a máscara de um CNPJ e normaliza para maiúsculas. Não valida nada —
 * só limpa separadores `.`, `-`, `/` e espaços.
 */
export function sanitizeCNPJ(value: string): string {
  if (typeof value !== 'string') return '';
  return value.replace(/\s/g, '').replace(CNPJ_MASK_CHARS, '').toUpperCase();
}

/** `true` se o CNPJ (já sanitizado) contém ao menos uma letra na base. */
export function isAlphanumericCNPJ(sanitized: string): boolean {
  return /[A-Z]/.test(sanitized);
}

/**
 * Calcula os 2 dígitos verificadores de um CNPJ (base de 12 posições
 * alfanuméricas, SEM DV). Retorna a string de 2 dígitos ou `null` se a base
 * não tiver o formato esperado. Mesmo algoritmo para numérico e alfanumérico —
 * um CNPJ numérico é só o caso particular em que a base não tem letras.
 */
export function computeCNPJCheckDigits(base: string): string | null {
  const sanitized = sanitizeCNPJ(base);
  if (!CNPJ_BASE_REGEX.test(sanitized)) return null;

  let sum1 = 0;
  let sum2 = 0;
  for (let i = 0; i < CNPJ_BASE_LENGTH; i++) {
    const value = sanitized.charCodeAt(i) - ASCII_ZERO;
    sum1 += value * CNPJ_DV_WEIGHTS[i + 1];
    sum2 += value * CNPJ_DV_WEIGHTS[i];
  }
  const dv1 = sum1 % 11 < 2 ? 0 : 11 - (sum1 % 11);
  sum2 += dv1 * CNPJ_DV_WEIGHTS[CNPJ_BASE_LENGTH];
  const dv2 = sum2 % 11 < 2 ? 0 : 11 - (sum2 % 11);
  return `${dv1}${dv2}`;
}

/**
 * Valida um CNPJ nos DOIS padrões (antigo numérico e novo alfanumérico),
 * conferindo os dígitos verificadores. Aceita com ou sem máscara.
 *
 * Rejeita o CNPJ "zerado" (base toda em '0') e sequências numéricas de dígito
 * repetido (00000000000000, 11111111111111, ...), consistente com a validação
 * de CPF existente.
 */
export function validateCNPJ(cnpj: string): boolean {
  if (typeof cnpj !== 'string') return false;
  const sanitized = sanitizeCNPJ(cnpj);
  if (!CNPJ_FULL_REGEX.test(sanitized)) return false;

  // CNPJ zerado e, para o caso numérico, qualquer dígito repetido são inválidos.
  if (sanitized === '00000000000000') return false;
  if (!isAlphanumericCNPJ(sanitized) && /^(\d)\1{13}$/.test(sanitized)) return false;

  const base = sanitized.substring(0, CNPJ_BASE_LENGTH);
  const informedDV = sanitized.substring(CNPJ_BASE_LENGTH);
  return computeCNPJCheckDigits(base) === informedDV;
}

/** `true` se é um CNPJ válido E contém letras (padrão alfanumérico novo). */
export function isValidAlphanumericCNPJ(cnpj: string): boolean {
  const sanitized = sanitizeCNPJ(cnpj);
  return isAlphanumericCNPJ(sanitized) && validateCNPJ(sanitized);
}

/**
 * Aplica a máscara visual de CNPJ `XX.XXX.XXX/XXXX-XX` a um CNPJ numérico OU
 * alfanumérico. Preserva as letras da base (diferente de uma máscara que só
 * reposiciona dígitos). Entrada parcial é mascarada progressivamente.
 */
export function maskCNPJValue(value: string): string {
  const s = sanitizeCNPJ(value).slice(0, 14);
  if (s.length <= 2) return s;
  if (s.length <= 5) return `${s.slice(0, 2)}.${s.slice(2)}`;
  if (s.length <= 8) return `${s.slice(0, 2)}.${s.slice(2, 5)}.${s.slice(5)}`;
  if (s.length <= 12) return `${s.slice(0, 2)}.${s.slice(2, 5)}.${s.slice(5, 8)}/${s.slice(8)}`;
  return `${s.slice(0, 2)}.${s.slice(2, 5)}.${s.slice(5, 8)}/${s.slice(8, 12)}-${s.slice(12)}`;
}

// ───────────────────────────── Placa / Chassi ──────────────────────────────

/** Placa padrão ANTIGO: 3 letras + 4 dígitos (AAA9999). */
const PLACA_ANTIGA_REGEX = /^[A-Z]{3}\d{4}$/;

/** Placa padrão MERCOSUL: 3 letras + dígito + letra + 2 dígitos (AAA9A99). */
const PLACA_MERCOSUL_REGEX = /^[A-Z]{3}\d[A-Z]\d{2}$/;

/**
 * Chassi/VIN: 17 posições no alfabeto VIN (sem I, O, Q — evitam confusão
 * visual com 1/0) e com ao menos uma letra (nenhum VIN real é inteiramente
 * numérico; sem isso um ID interno de 17 dígitos passaria como chassi).
 */
const CHASSI_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/;

/** Remove espaços/hífens e normaliza para maiúsculas (placa e chassi). */
function sanitizeVeiculo(value: string): string {
  return value.replace(/[\s-]/g, '').toUpperCase();
}

/** `true` se a placa está no padrão antigo (AAA9999). */
export function isPlacaAntiga(placa: string): boolean {
  if (typeof placa !== 'string') return false;
  return PLACA_ANTIGA_REGEX.test(sanitizeVeiculo(placa));
}

/** `true` se a placa está no padrão Mercosul (AAA9A99). */
export function isPlacaMercosul(placa: string): boolean {
  if (typeof placa !== 'string') return false;
  return PLACA_MERCOSUL_REGEX.test(sanitizeVeiculo(placa));
}

/**
 * Valida o FORMATO de uma placa brasileira nos dois padrões (antigo AAA9999 e
 * Mercosul AAA9A99). Aceita espaço/hífen entre letras e dígitos. Validação de
 * formato apenas — a existência real da placa é conferida pela Snap.
 */
export function validatePlaca(placa: string): boolean {
  return isPlacaAntiga(placa) || isPlacaMercosul(placa);
}

/**
 * Valida o FORMATO de um chassi/VIN (17 alfanuméricos, alfabeto VIN, ao menos
 * uma letra). Formato apenas — a Snap confere o chassi de fato.
 */
export function validateChassi(chassi: string): boolean {
  if (typeof chassi !== 'string') return false;
  const clean = sanitizeVeiculo(chassi);
  return CHASSI_REGEX.test(clean) && /[A-HJ-NPR-Z]/.test(clean);
}
